import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { RegionSelector } from "@/components/RegionSelector";

const navLinks = [
  { label: "Properties", href: "/properties", isPage: true },
  { label: "Services", href: "#services", isPage: false },
  { label: "Destinations", href: "#destinations", isPage: false },
  { label: "Heritage", href: "#heritage", isPage: false },
  { label: "Team", href: "/team", isPage: true },
  { label: "Contact", href: "#contact", isPage: false },
];

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, link: typeof navLinks[0]) => {
    if (link.isPage) {
      // Let the Link component handle page navigation
      setIsMobileMenuOpen(false);
      return;
    }

    e.preventDefault();
    const targetId = link.href.replace("#", "");

    // If not on homepage, navigate to homepage first
    if (location.pathname !== "/") {
      navigate("/" + link.href);
      setIsMobileMenuOpen(false);
      return;
    }

    const element = document.getElementById(targetId);
    if (element) {
      const navHeight = 80;
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: elementPosition - navHeight,
        behavior: "smooth",
      });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? "bg-background/95 backdrop-blur-md shadow-subtle py-4"
            : "bg-transparent py-6"
        }`}
      >
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between">
            <Link to="/">
              <Logo size="md" variant={isScrolled ? "dark" : "light"} />
            </Link>

            <div className="hidden lg:flex items-center gap-10">
              {navLinks.map((link) =>
                link.isPage ? (
                  <Link
                    key={link.label}
                    to={link.href}
                    className="font-body text-sm uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground transition-colors duration-300 relative group"
                  >
                    {link.label}
                    <span className="absolute -bottom-1 left-0 w-0 h-px bg-gold group-hover:w-full transition-all duration-300" />
                  </Link>
                ) : (
                  <a
                    key={link.label}
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link)}
                    className="font-body text-sm uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground transition-colors duration-300 relative group"
                  >
                    {link.label}
                    <span className="absolute -bottom-1 left-0 w-0 h-px bg-gold group-hover:w-full transition-all duration-300" />
                  </a>
                )
              )}
            </div>

            {/* Region Selector & CTA Button */}
            <div className="hidden lg:flex items-center gap-4">
              <RegionSelector />
              <Button variant="luxury" size="lg" asChild>
                <a href="#contact" onClick={(e) => handleNavClick(e, { label: "Contact", href: "#contact", isPage: false })}>
                  Private Inquiry
                </a>
              </Button>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-foreground"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-background pt-24 lg:hidden"
          >
            <div className="container mx-auto px-6">
              <div className="flex flex-col gap-6">
                {navLinks.map((link, index) =>
                  link.isPage ? (
                    <motion.div
                      key={link.label}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Link
                        to={link.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="font-display text-3xl text-foreground hover:text-gold transition-colors block"
                      >
                        {link.label}
                      </Link>
                    </motion.div>
                  ) : (
                    <motion.a
                      key={link.label}
                      href={link.href}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      onClick={(e) => handleNavClick(e, link)}
                      className="font-display text-3xl text-foreground hover:text-gold transition-colors"
                    >
                      {link.label}
                    </motion.a>
                  )
                )}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="pt-6"
                >
                  <Button variant="luxury" size="xl" className="w-full" asChild>
                    <a href="#contact" onClick={(e) => handleNavClick(e, { label: "Contact", href: "#contact", isPage: false })}>
                      Private Inquiry
                    </a>
                  </Button>
                </motion.div>
                
                {/* Mobile Region Selector */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                >
                  <RegionSelector variant="mobile" />
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
